import { Component, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { type ShiftCreate, type Shift } from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { type FormCreateProps } from "../../utils/form-create-props";
import { createUuid } from "../../utils/uuid";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormCreateShiftProps extends FormCreateProps<Shift> {}

import { store } from "../../store";

@observer
export class FormCreateShift extends Component<FormCreateShiftProps> {
    @observable public accessor label = "";

    private validationId = createUuid();

    constructor(props: FormCreateShiftProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryShifts.validation.create.updateModel(this.validationId, this.shiftCreate);
    }

    componentDidMount(): void {
        store.repositoryShifts.validation.create.initializeModel(
            this.validationId,
            this.shiftCreate,
        );
    }

    @computed private get shiftCreate(): ShiftCreate {
        return pick(["label"], this as FormCreateShift);
    }

    @action.bound
    private async createShift(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const shift = await store.repositoryShifts.create(this.shiftCreate);
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(shift);
        }
    }

    @action.bound private reset(): void {
        this.label = "";
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreateShift.submit.text")}
                validation={store.repositoryShifts.validation.create}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.createShift}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formCreateShift.label.label")}
                        {...doubleBindString<FormCreateShift>(this, "label", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShifts.validation.create.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreateShift.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
